import API from "../config/api";

const LEGACY_STORAGE_KEY = "ecovanta_notifications_v1";
const MIGRATION_KEY = "ecovanta_notifications_server_migrated_v1";
const EVENT_NAME = "ecovanta:notifications-changed";

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("ecovanta_token") ||
    ""
  );
}

function authHeaders(includeJson = true) {
  const token = getToken();

  return {
    ...(includeJson
      ? {
          "Content-Type": "application/json",
        }
      : {}),
    ...(token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}),
  };
}

async function parseResponse(response, fallbackMessage) {
  const contentType = response.headers.get("content-type") || "";
  const raw = await response.text();

  let data = {};

  if (raw && contentType.includes("application/json")) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    throw new Error(
      data.error ||
        data.message ||
        fallbackMessage ||
        `HTTP ${response.status}`
    );
  }

  return data;
}

function normalizeNotification(item) {
  return {
    ...item,
    id: item.id || item._id,
    read:
      item.read !== undefined
        ? Boolean(item.read)
        : Boolean(item.isRead),
  };
}

function emitChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
}

export async function getNotifications() {
  const response = await fetch(`${API}/notifications?limit=100`, {
    headers: authHeaders(false),
    credentials: "include",
  });

  const data = await parseResponse(
    response,
    "Failed to fetch notifications"
  );

  return Array.isArray(data)
    ? data.map(normalizeNotification)
    : [];
}

export async function getUnreadNotificationCount() {
  const response = await fetch(
    `${API}/notifications/unread-count`,
    {
      headers: authHeaders(false),
      credentials: "include",
    }
  );

  const data = await parseResponse(
    response,
    "Failed to fetch unread notification count"
  );

  return Number(data.unreadCount || 0);
}

export async function addNotification(notification) {
  try {
    const response = await fetch(`${API}/notifications`, {
      method: "POST",
      headers: authHeaders(true),
      credentials: "include",
      body: JSON.stringify({
        type: notification.type || "system",
        priority: notification.priority || "normal",
        title: notification.title || "Ecovanta update",
        message: notification.message || "",
        targetPage: notification.targetPage || "Dashboard",
        targetId: notification.targetId || "",
        dedupeKey: notification.dedupeKey || "",
        metadata: notification.metadata || {},
      }),
    });

    const data = normalizeNotification(
      await parseResponse(
        response,
        "Failed to create notification"
      )
    );

    emitChange();

    return data;
  } catch (error) {
    console.warn(
      "Notification could not be persisted:",
      error.message
    );
    return null;
  }
}

export async function addNotificationOnce(
  notification,
  dedupeKey
) {
  return addNotification({
    ...notification,
    dedupeKey: String(
      dedupeKey ||
        notification?.dedupeKey ||
        ""
    ).trim(),
  });
}

export async function markNotificationRead(notificationId) {
  const response = await fetch(
    `${API}/notifications/${notificationId}/read`,
    {
      method: "PATCH",
      headers: authHeaders(true),
      credentials: "include",
    }
  );

  const data = normalizeNotification(
    await parseResponse(
      response,
      "Failed to mark notification as read"
    )
  );

  emitChange();

  return data;
}

export async function markAllNotificationsRead() {
  const response = await fetch(
    `${API}/notifications/read-all`,
    {
      method: "PATCH",
      headers: authHeaders(true),
      credentials: "include",
    }
  );

  const data = await parseResponse(
    response,
    "Failed to mark all notifications as read"
  );

  emitChange();

  return data;
}

export async function removeNotification(notificationId) {
  const response = await fetch(
    `${API}/notifications/${notificationId}`,
    {
      method: "DELETE",
      headers: authHeaders(false),
      credentials: "include",
    }
  );

  const data = await parseResponse(
    response,
    "Failed to delete notification"
  );

  emitChange();

  return data;
}

export async function clearNotifications() {
  const response = await fetch(`${API}/notifications`, {
    method: "DELETE",
    headers: authHeaders(false),
    credentials: "include",
  });

  const data = await parseResponse(
    response,
    "Failed to clear notifications"
  );

  emitChange();

  return data;
}

export async function migrateLegacyNotifications() {
  if (typeof window === "undefined") return;

  if (localStorage.getItem(MIGRATION_KEY) === "done") {
    return;
  }

  const raw = localStorage.getItem(LEGACY_STORAGE_KEY);

  if (!raw) {
    localStorage.setItem(MIGRATION_KEY, "done");
    return;
  }

  try {
    const items = JSON.parse(raw);

    if (Array.isArray(items)) {
      for (const item of items.slice(0, 25)) {
        await addNotification({
          ...item,
          dedupeKey:
            item.dedupeKey ||
            `legacy:${item.id || item.createdAt}`,
        });
      }
    }

    localStorage.setItem(MIGRATION_KEY, "done");
  } catch (error) {
    console.warn(
      "Legacy notification migration skipped:",
      error.message
    );
  }
}

export const notificationEventName = EVENT_NAME;